import React, { useState, useRef, useEffect } from 'react'

type Grid = number[][] | undefined
type Coordinates = [number, number] | undefined

interface Shape {
  grid: Grid
  coordinates: Coordinates
  moveInterval: NodeJS.Timeout
}

interface TetrisGame {
  gameOver: boolean
  board: Grid
  renderInterval: NodeJS.Timeout
  currentShape: Shape
  level: number
  score: number
}

const WIDTH: number = 300
const HEIGHT: number = 600
const COLS: number = 10
const ROWS: number = 20
const BASE_SPEED: number = 400
const COLOURS: number[][] = [
  [0, 255, 255],
  [255, 165, 0],
  [0, 0, 255],
  [255, 255, 0],
  [255, 0, 0],
  [0, 255, 0],
  [128, 0, 128]
]

const INITIAL_GAME_STATE: TetrisGame = {
  gameOver: false,
  board: undefined,
  renderInterval: null,
  currentShape: {
    grid: undefined,
    coordinates: undefined,
    moveInterval: null
  },
  level: 1,
  score: 0
}
const SHAPES: number[][] = [
  [1, 1, 1, 1],
  [1, 1, 1, 0, 1],
  [1, 1, 1, 0, 0, 0, 1],
  [1, 1, 0, 0, 1, 1],
  [1, 1, 0, 0, 0, 1, 1],
  [0, 1, 1, 0, 1, 1],
  [0, 1, 0, 0, 1, 1, 1]
]

enum Score {
  SINGLE = 1,
  DOUBLE = 2,
  TRIPLE = 3,
  TETRIS = 4,
}

enum Validation {
  VALID,
  INVALID
}

enum Controls {
  DROP = 'ArrowDown',
  LEFT = 'ArrowLeft',
  RIGHT = 'ArrowRight',
  ROTATE = 'ArrowUp'
}

const SCORES: Map<string, number> = new Map([
  [Score[1], 40],
  [Score[2], 100],
  [Score[3], 300],
  [Score[4], 1200]
])

const LEVEL_CAPS: number[] = [
  0,
  400,
  1000,
  3000,
  12000,
  36000,
  108000,
  324000,
  972000,
  2916000
]

export const Tetris: React.FC = (): JSX.Element => {
  const canvasRef = useRef<HTMLCanvasElement>()
  const [{ gameOver, board, level, score }, updateGame] = useState<TetrisGame>(INITIAL_GAME_STATE)

  const clearIntervals = (): void => updateGame((game: TetrisGame) => {
    const { renderInterval, currentShape } = game
    clearInterval(renderInterval)
    clearInterval(currentShape.moveInterval)

    return game
  })

  const generateShape = (level: number): Shape => {
    const id: number = Math.floor(Math.random() * SHAPES.length)
    const shape: number[] = SHAPES[id]

    return {
      grid: [...Array(4)].map(() => [...Array(4)].map(() => 0))
        .map((_, y): number[] =>
          _.map((_, x): number =>
            (shape[4 * y + x] as unknown) as boolean ? id + 1 : 0
          )
        ),
      coordinates: [Math.floor(Math.random() * (COLS - 5)), 0],
      moveInterval: setInterval(() => {
        moveShape([0, 1])
        clearLines()
      }, BASE_SPEED - level * 30)
    }
  }

  const rotateShape = (): void => updateGame((game: TetrisGame) => {
    const { currentShape } = game

    return {
      ...game,
      currentShape: {
        ...currentShape,
        ...{
          grid: currentShape.grid.map((_, y): number[] =>
            _.map((_, x): number => currentShape.grid[3 - x][y])
          )
        }
      }
    }
  })

  const moveShape = (offset: Coordinates): void => updateGame((game: TetrisGame) => {
    const { board, renderInterval, currentShape, level } = game
    const coordinates: Coordinates = [currentShape.coordinates[0] + offset[0], currentShape.coordinates[1] + offset[1]]

    const move = currentShape.grid.some((_, y): boolean =>
      _.some((cell, x): boolean => cell > 0 && (
        board[y + coordinates[1]] === undefined ||
        board[y + coordinates[1]][x + coordinates[0]] === undefined ||
        board[y + coordinates[1]][x + coordinates[0]] > 0 ||
        x + coordinates[0] < 0 ||
        x + coordinates[0] >= COLS ||
        y + coordinates[1] >= ROWS
      ))
    ) ? Validation.INVALID : Validation.VALID

    switch (move) {
      case Validation.VALID:
        return {
          ...game,
          ...{
            currentShape: {
              ...currentShape,
              ...{
                coordinates
              }
            }
          }
        }
      case Validation.INVALID:
        if (offset[0] !== 0) {
          return game
        }

        currentShape.grid.forEach((_, y): void =>
          _.forEach((cell, x): void => {
            if (cell > 0) {
              board[y + currentShape.coordinates[1]][x + currentShape.coordinates[0]] = cell
            }
          })
        )

        clearInterval(currentShape.moveInterval)

        if (currentShape.coordinates[1] === 0) {
          clearInterval(renderInterval)

          return {
            ...game,
            ...{
              gameOver: true
            }
          }
        }

        return {
          ...game,
          ...{
            board,
            currentShape: generateShape(level)
          }
        }
    }
  })

  const clearLines = (): void => updateGame((game: TetrisGame) => {
    const clearBoard = game.board.filter((row) => row.some((cell) => cell === 0))
    const lines = ROWS - clearBoard.length

    if (lines > 0) {
      const score = game.score + (SCORES.get(Score[lines]) * game.level)
      const level = LEVEL_CAPS.findIndex((cap) => score < cap)

      return {
        ...game,
        ...{
          board: [
            ...[...Array(lines)].map(() => [...Array(COLS)].map(() => 0)),
            ...clearBoard
          ],
          level,
          score
        }
      }
    }

    return game
  })

  const render = (): void => updateGame((game: TetrisGame) => {
    const { board, currentShape } = game
    const ctx = canvasRef?.current?.getContext('2d')
    const blockWidth: number = WIDTH / COLS
    const blockHeight: number = HEIGHT / ROWS

    const drawCells = (grid: Grid, coordinates?: Coordinates): void =>
      grid.forEach((_, y): void =>
        _.forEach((cell, x): void => {
          const [X, Y] = (coordinates as unknown) as boolean ? [coordinates[0] + x, coordinates[1] + y] : [x, y]

          if ((cell as unknown) as boolean) {
            const colour = COLOURS[cell - 1].join(',')
            ctx.strokeStyle = `rgba(${colour}, 0.7)`
            ctx.fillStyle = `rgba(${colour}, 1)`
            ctx.fillRect(blockWidth * X, blockHeight * Y, blockWidth - 1, blockHeight - 1)
            ctx.strokeRect(blockWidth * X, blockHeight * Y, blockWidth - 1, blockHeight - 1)
          }
        })
      )

    ctx.clearRect(0, 0, WIDTH, HEIGHT)

    drawCells(board)
    drawCells(currentShape.grid, currentShape.coordinates)

    return game
  })

  const onKeyPress = (event: KeyboardEvent): void => {
    event.preventDefault()

    switch (event.key) {
      case Controls.DROP:
        return moveShape([0, 1])
      case Controls.LEFT:
        return moveShape([-1, 0])
      case Controls.RIGHT:
        return moveShape([1, 0])
      case Controls.ROTATE:
        return rotateShape()
    }
  }

  const newGame = (): void => {
    clearIntervals()
    updateGame({
      gameOver: false,
      board: [...Array(ROWS)].map(() => [...Array(COLS)].map(() => 0)),
      renderInterval: setInterval(render, 30),
      currentShape: generateShape(1),
      level: 1,
      score: 0
    })
  }

  useEffect(() => {
    if (board !== undefined) {
      window?.addEventListener('keydown', onKeyPress)
      return () => window?.removeEventListener('keydown', onKeyPress)
    }
  }, [board])

  return (
    <section
      style={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative'
      }}
    >
      <canvas ref={canvasRef} width={WIDTH} height={HEIGHT} style={{ backgroundColor: 'black' }} />
      <div
        style={{
          alignItems: 'center',
          background: (gameOver || board === undefined) ? 'rgba(0, 0, 0, 0.5)' : 'none',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'monospace',
          height: HEIGHT,
          justifyContent: 'space-between',
          left: '50%',
          padding: 20,
          position: 'absolute',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: WIDTH
        }}
      >
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.5)',
            border: '2px solid white',
            boxSizing: 'border-box',
            color: 'white',
            fontSize: '11am 6px',
            padding: 10,
            width: '100%'
          }}
        >
          Level: {level}
          <br />
            Score: {score}
        </div>
        {gameOver && (
          <div
            style={{
              textAlign: 'center'
            }}
          >
            <p
              style={{
                color: 'white',
                fontSize: '24px'
              }}
            >(ノಠ益ಠ)ノ彡┻━┻</p>
            <p
              style={{
                color: 'white',
                fontSize: '36px'
              }}
            >Game Over</p>
          </div>
        )}
        {(gameOver || board === undefined) && (
          <button
            onClick={newGame}
            style={{
              background: 'none',
              border: '2px solid white',
              borderRadius: 10,
              color: 'white',
              fontSize: '24px',
              lineHeight: '48px',
              outline: 0,
              padding: 0,
              width: 120
            }}
          >
            Play
          </button>
        )}
      </div>
    </section >
  )
}
